/**
 * AssetMultiSelect Component
 * Multi-select checkbox component for selecting assets to compare
 */

import { useState, useMemo } from 'react'
import { useAssetStore, useComparisonStore } from '@/store'
import { Search, Check, X, ChevronDown, ChevronUp } from 'lucide-react'
import clsx from 'clsx'

interface AssetMultiSelectProps {
  maxSelection?: number
  className?: string
  onSelectionChange?: (assetIds: string[]) => void
}

export default function AssetMultiSelect({
  maxSelection = 5,
  className,
  onSelectionChange
}: AssetMultiSelectProps) {
  const { assets } = useAssetStore()
  const { selectedAssetIds, toggleAsset, clearSelection } = useComparisonStore()
  const [searchTerm, setSearchTerm] = useState('')
  const [isExpanded, setIsExpanded] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const filteredAssets = useMemo(() => {
    // Guard against assets not being an array
    if (!Array.isArray(assets)) return []
    return assets.filter(asset => {
      const matchesSearch =
        asset.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.name?.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesStatus = statusFilter === 'all' || asset.status === statusFilter
      return matchesSearch && matchesStatus
    })
  }, [assets, searchTerm, statusFilter])

  const handleToggle = (assetId: string) => {
    if (selectedAssetIds.includes(assetId) || selectedAssetIds.length < maxSelection) {
      toggleAsset(assetId)
      onSelectionChange?.([...selectedAssetIds.filter(id => id !== assetId),
        ...(selectedAssetIds.includes(assetId) ? [] : [assetId])])
    }
  }

  const handleClearAll = () => {
    clearSelection()
    onSelectionChange?.([])
  }

  const statusCounts = useMemo(() => {
    // Guard against assets not being an array
    if (!Array.isArray(assets)) return {}
    return assets.reduce((acc, asset) => {
      acc[asset.status] = (acc[asset.status] || 0) + 1
      return acc
    }, {} as Record<string, number>)
  }, [assets])

  return (
    <div className={clsx('bg-surface-800 rounded-lg border border-surface-700', className)}>
      {/* Header */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-surface-700/50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-surface-200">Select Assets</h3>
          <span className="text-xs text-surface-400">
            ({selectedAssetIds.length}/{maxSelection})
          </span>
        </div>
        <div className="flex items-center gap-2">
          {selectedAssetIds.length > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); handleClearAll() }}
              className="text-xs text-surface-400 hover:text-surface-200 flex items-center gap-1"
            >
              <X size={12} />
              Clear
            </button>
          )}
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {isExpanded && (
        <>
          {/* Search and Filter */}
          <div className="px-3 pb-2 space-y-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-surface-400" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-surface-900 border border-surface-600 rounded focus:border-primary-500 focus:outline-none"
              />
            </div>

            {/* Status Filter Pills */}
            <div className="flex gap-1 flex-wrap">
              {['all', 'healthy', 'warning', 'critical'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={clsx(
                    'px-2 py-0.5 text-xs rounded-full transition-colors',
                    statusFilter === status
                      ? status === 'all' ? 'bg-primary-500/20 text-primary-400 border border-primary-500/50'
                      : status === 'healthy' ? 'bg-status-healthy/20 text-status-healthy border border-status-healthy/50'
                      : status === 'warning' ? 'bg-status-warning/20 text-status-warning border border-status-warning/50'
                      : 'bg-status-critical/20 text-status-critical border border-status-critical/50'
                      : 'bg-surface-700 text-surface-400 hover:bg-surface-600'
                  )}
                >
                  {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                  {status !== 'all' && statusCounts[status] && (
                    <span className="ml-1">({statusCounts[status]})</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Asset List */}
          <div className="max-h-64 overflow-y-auto border-t border-surface-700">
            {filteredAssets.length === 0 ? (
              <div className="p-4 text-center text-sm text-surface-400">
                No assets found
              </div>
            ) : (
              <div className="divide-y divide-surface-700/50">
                {filteredAssets.map(asset => {
                  const isSelected = selectedAssetIds.includes(asset.id)
                  const isDisabled = !isSelected && selectedAssetIds.length >= maxSelection

                  return (
                    <div
                      key={asset.id}
                      onClick={() => !isDisabled && handleToggle(asset.id)}
                      className={clsx(
                        'flex items-center gap-3 px-3 py-2 transition-colors',
                        isDisabled
                          ? 'opacity-50 cursor-not-allowed'
                          : 'cursor-pointer hover:bg-surface-700/50',
                        isSelected && 'bg-primary-500/10'
                      )}
                    >
                      {/* Checkbox */}
                      <div className={clsx(
                        'w-4 h-4 rounded border flex items-center justify-center transition-colors',
                        isSelected
                          ? 'bg-primary-500 border-primary-500'
                          : 'border-surface-500 bg-surface-800'
                      )}>
                        {isSelected && <Check size={12} className="text-white" />}
                      </div>

                      {/* Asset Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-surface-200">
                            {asset.id}
                          </span>
                          <span className={clsx(
                            'w-2 h-2 rounded-full',
                            asset.status === 'healthy' && 'bg-status-healthy',
                            asset.status === 'warning' && 'bg-status-warning',
                            asset.status === 'critical' && 'bg-status-critical'
                          )} />
                        </div>
                        {asset.name && (
                          <p className="text-xs text-surface-400 truncate">{asset.name}</p>
                        )}
                      </div>

                      {/* Health Score */}
                      {asset.health_score !== undefined && (
                        <span className={clsx(
                          'text-xs font-mono',
                          asset.health_score >= 80 ? 'text-status-healthy' :
                          asset.health_score >= 60 ? 'text-status-warning' :
                          'text-status-critical'
                        )}>
                          {asset.health_score}%
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Selection Summary */}
          {selectedAssetIds.length > 0 && (
            <div className="p-2 border-t border-surface-700 bg-surface-900/50">
              <div className="flex flex-wrap gap-1">
                {selectedAssetIds.map(id => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary-500/20 text-primary-400 rounded"
                  >
                    {id}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleToggle(id) }}
                      className="hover:text-primary-300"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
